/**
 * Credits page — full status + history + credit packs.
 * Pack purchase = "coming soon" (Stripe not wired yet) as per sprint constraint.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, ArrowUpRight, ShoppingCart, Clock, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useCredits, formatResetDate } from '@/lib/useCredits';
import { useT } from '@/lib/i18n';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

const TYPE_LABELS = {
  monthly_reset:      { fr: 'Renouvellement mensuel',    en: 'Monthly renewal' },
  video_processing:   { fr: 'Traitement vidéo',          en: 'Video processing' },
  refund:             { fr: 'Remboursement',             en: 'Refund' },
  credit_purchase:    { fr: 'Achat de crédits',          en: 'Credit purchase' },
  admin_grant:        { fr: 'Crédits offerts (admin)',   en: 'Admin grant' },
  admin_adjustment:   { fr: 'Ajustement admin',          en: 'Admin adjustment' },
};

const CreditsPage = () => {
  const { summary, loading, refresh } = useCredits();
  const { lang } = useT();
  const [history, setHistory] = useState([]);
  const [packs, setPacks] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [h, p] = await Promise.all([
          api.get('/credits/history?limit=30'),
          api.get('/credits/packs'),
        ]);
        setHistory(h.data?.history || []);
        setPacks(p.data?.packs || null);
      } catch { /* silent */ }
    })();
  }, []);

  const handleBuy = (packKey) => {
    toast.info(lang === 'fr'
      ? 'Achat en crédits — bientôt disponible via Stripe.'
      : 'Credit purchase — coming soon via Stripe.');
    // Placeholder: when Stripe is wired, POST to /payments/credit-packs/checkout {pack: packKey}
    void packKey;
  };

  if (loading || !summary) {
    return <div className="max-w-6xl mx-auto px-6 py-10" data-testid="credits-page-loading">…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="credits-page">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mb-10">
        <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">
          {lang === 'fr' ? 'Crédits' : 'Credits'}
        </span>
        <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 tracking-tighter">
          {lang === 'fr' ? 'Tes crédits HighlightAI' : 'Your HighlightAI credits'}
        </h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
          {lang === 'fr'
            ? '1 crédit = 1 minute de vidéo traitée. Les crédits mensuels se renouvellent automatiquement. Les crédits achetés sont conservés indéfiniment.'
            : '1 credit = 1 minute of processed video. Monthly credits renew automatically. Purchased credits never expire.'}
        </p>
      </motion.div>

      {/* Three stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <StatCard icon={Zap} label={lang === 'fr' ? 'Mensuels' : 'Monthly'} value={`${summary.monthly_credits} / ${summary.monthly_capacity}`} accent="lime" testid="stat-monthly" />
        <StatCard icon={Wallet} label={lang === 'fr' ? 'Achetés' : 'Purchased'} value={summary.purchased_credits} testid="stat-purchased" />
        <StatCard icon={TrendingUp} label={lang === 'fr' ? 'Total disponible' : 'Total available'} value={summary.total_available} testid="stat-total" />
      </div>

      {/* Renewal */}
      <div className="mb-10 flex items-center gap-3 text-sm text-zinc-500">
        <Clock className="w-4 h-4" />
        <span>
          {lang === 'fr' ? 'Prochain renouvellement le' : 'Next renewal on'}{' '}
          <span className="text-zinc-300" data-testid="credits-period-end">{formatResetDate(summary.period_end, lang)}</span>
        </span>
      </div>

      {/* Credit packs */}
      <div className="mb-14">
        <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-4">
          {lang === 'fr' ? 'Acheter des crédits' : 'Buy credits'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {packs && Object.entries(packs).map(([key, p]) => (
            <div
              key={key}
              className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 hover:border-white/10 transition-colors"
              data-testid={`pack-${key}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart className="w-4 h-4 text-[#D4FF00]" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{key.replace('_', ' ')}</span>
              </div>
              <div className="font-heading text-3xl font-black">{p.credits}</div>
              <div className="text-xs text-zinc-500 mb-4">{lang === 'fr' ? 'crédits' : 'credits'}</div>
              <div className="font-heading text-xl mb-4">{p.price_eur} €</div>
              <Button
                onClick={() => handleBuy(key)}
                className="w-full bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold"
                data-testid={`pack-buy-${key}`}
              >
                {lang === 'fr' ? 'Acheter' : 'Buy'} <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[10px] text-zinc-600 mt-3 text-center">
                {lang === 'fr' ? 'Paiement Stripe bientôt' : 'Stripe checkout coming soon'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
            {lang === 'fr' ? 'Historique' : 'History'}
          </h2>
          <button onClick={refresh} className="text-[10px] uppercase text-zinc-500 hover:text-white font-mono">
            {lang === 'fr' ? 'Rafraîchir' : 'Refresh'}
          </button>
        </div>
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm" data-testid="credits-history-table">
            <thead className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-white/5">
              <tr>
                <th className="p-3">{lang === 'fr' ? 'Date' : 'Date'}</th>
                <th className="p-3">{lang === 'fr' ? 'Type' : 'Type'}</th>
                <th className="p-3 text-right">{lang === 'fr' ? 'Montant' : 'Amount'}</th>
                <th className="p-3">{lang === 'fr' ? 'Raison' : 'Reason'}</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-zinc-500 text-xs">
                  {lang === 'fr' ? 'Aucun mouvement pour le moment.' : 'No activity yet.'}
                </td></tr>
              ) : history.map((h) => (
                <tr key={h.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`hist-row-${h.id}`}>
                  <td className="p-3 text-xs text-zinc-400 font-mono whitespace-nowrap">
                    {formatResetDate(h.created_at, lang)}
                  </td>
                  <td className="p-3 text-xs text-zinc-300">
                    {(TYPE_LABELS[h.type] || {})[lang] || h.type}
                  </td>
                  <td className={`p-3 text-right font-mono text-sm tabular-nums ${h.delta > 0 ? 'text-[#D4FF00]' : h.delta < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {h.delta > 0 ? <TrendingUp className="w-3 h-3" /> : h.delta < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                      {h.delta > 0 ? '+' : ''}{h.delta}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-zinc-500 max-w-[300px] truncate">{h.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    className={`rounded-2xl border ${accent === 'lime' ? 'border-[#D4FF00]/20 bg-[#D4FF00]/5' : 'border-white/5 bg-white/[0.02]'} p-5`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-3.5 h-3.5 ${accent === 'lime' ? 'text-[#D4FF00]' : 'text-zinc-500'}`} />
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
    </div>
    <div className={`font-heading text-3xl font-black tabular-nums ${accent === 'lime' ? 'text-[#D4FF00]' : ''}`}>{value}</div>
  </div>
);

export default CreditsPage;
